import React, {useEffect, useState} from "react";
import {
    BankMovementRestControllerApiFactory,
    ReconciliationProposalDto,
} from "arteco-api-client-ts";
import {useApiManager} from "../../../../components/utils/api";
import {
    Box,
    Button,
    LinearProgress,
    Link,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import {TaskAlt} from "@mui/icons-material";
import {useNavigate} from "react-router-dom";
import {fmtEur} from "../../../../components/utils/numbers";
import {dateToString} from "../../../../components/utils/dates";
import {
    btnSx,
    EmptyState,
    ListCard,
    Page,
    PageHeader,
    Panel,
    SkeletonRows,
    SoftChip,
    tableContainerSx,
    tableSx,
} from "../../../../components/kit";

const ScoreBar = ({score}: {score: number}) => (
    <Box display="flex" alignItems="center" gap={1}>
        <LinearProgress
            variant="determinate"
            value={score}
            color={score >= 80 ? "success" : score >= 50 ? "warning" : "error"}
            sx={{flex: 1, height: 6, borderRadius: 3, "& .MuiLinearProgress-bar": {borderRadius: 3}}}
        />
        <Typography variant="caption" fontWeight={600} sx={{minWidth: 32, fontVariantNumeric: "tabular-nums"}}>{score}%</Typography>
    </Box>
);

const typeChip = (p: ReconciliationProposalDto) =>
    <SoftChip label={p.proposedType === "CLIENT_BILL" ? "Cliente" : "Proveedor"} tone={p.proposedType === "CLIENT_BILL" ? "info" : "warning"}/>;

export const ReconciliationPage = () => {
    const apiManager = useApiManager();
    const navigate = useNavigate();
    const theme = useTheme();
    const compact = useMediaQuery(theme.breakpoints.down("md"));
    const [proposals, setProposals] = useState<ReconciliationProposalDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [linking, setLinking] = useState<number | null>(null);

    const loadProposals = () => {
        setLoading(true);
        const ctrl = apiManager.factory(BankMovementRestControllerApiFactory);
        apiManager.execute(ctrl.proposalsBankMovement())
            .then(list => setProposals(list as ReconciliationProposalDto[]))
            .finally(() => setLoading(false));
    };

    useEffect(() => { loadProposals(); }, []);

    const handleAccept = (p: ReconciliationProposalDto) => {
        if (p.proposedType !== "CLIENT_BILL") return;
        setLinking(p.movementId!);
        const movCtrl = apiManager.factory(BankMovementRestControllerApiFactory);
        apiManager.execute(movCtrl.linkBillBankMovement(p.movementId!, p.proposedId!))
            .then(() => {
                setProposals(prev => prev.filter(x => x.movementId !== p.movementId));
            })
            .finally(() => setLinking(null));
    };

    const handleNavigate = (p: ReconciliationProposalDto) => {
        if (p.proposedType === "CLIENT_BILL") {
            navigate(`/app/admin/billing/bill/${p.proposedId}`);
        } else {
            navigate(`/app/admin/accounting/supplier-bill/${p.proposedId}`);
        }
    };

    const billLink = (p: ReconciliationProposalDto) => (
        <Link component="button" variant="body2" underline="hover" fontWeight={600} onClick={() => handleNavigate(p)}>
            {p.proposedNumber ?? `#${p.proposedId}`}
        </Link>
    );

    const action = (p: ReconciliationProposalDto) => p.proposedType === "CLIENT_BILL" ? (
        <Button size="small" variant="outlined" color="success" sx={btnSx}
                disabled={linking === p.movementId} onClick={() => handleAccept(p)}>
            Vincular
        </Button>
    ) : (
        <Typography variant="caption" color="text.secondary">Ver factura</Typography>
    );

    return (
        <Page>
            <PageHeader title="Conciliación bancaria asistida"
                        subtitle={!loading && proposals.length > 0
                            ? `${proposals.length} propuesta${proposals.length !== 1 ? "s" : ""} de coincidencia entre movimientos sin conciliar y facturas pendientes. La puntuación indica la fiabilidad.`
                            : "Propuestas de coincidencia entre movimientos bancarios sin conciliar y facturas pendientes."}/>

            {loading ? <SkeletonRows rows={5}/> : proposals.length === 0 ? (
                <Panel>
                    <EmptyState icon={<TaskAlt/>} title="No hay movimientos bancarios pendientes de conciliar."/>
                </Panel>
            ) : compact ? (
                <Stack spacing={1.5}>
                    {proposals.map((p, i) => (
                        <ListCard key={i}>
                            <Stack direction="row" justifyContent="space-between" spacing={1}>
                                <Typography variant="body2" fontWeight={600}>{dateToString(p.movementDate)}</Typography>
                                <Typography variant="body2" fontWeight={700} sx={{fontVariantNumeric: "tabular-nums"}}>{fmtEur(p.movementAmount)}</Typography>
                            </Stack>
                            <Typography variant="caption" color="text.secondary" display="block">{p.movementDescription}</Typography>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{mt: 1.5}}>
                                {typeChip(p)}
                                {billLink(p)}
                                <Box sx={{flex: 1}}/>
                                <Typography variant="body2" sx={{fontVariantNumeric: "tabular-nums"}}>{fmtEur(p.proposedAmount)}</Typography>
                            </Stack>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {p.proposedCounterparty} · {dateToString(p.proposedDate)}
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{mt: 1.5}}>
                                <Box sx={{flex: 1}}><ScoreBar score={p.matchScore ?? 0}/></Box>
                                {action(p)}
                            </Stack>
                        </ListCard>
                    ))}
                </Stack>
            ) : (
                <Panel>
                    <TableContainer sx={tableContainerSx}>
                        <Table size="small" sx={{...tableSx, tableLayout: "fixed"}}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Movimiento bancario</TableCell>
                                    <TableCell align="right" sx={{width: 120}}>Importe mov.</TableCell>
                                    <TableCell sx={{width: 170}}>Factura propuesta</TableCell>
                                    <TableCell sx={{width: 170}}>Contraparte</TableCell>
                                    <TableCell align="right" sx={{width: 120}}>Importe factura</TableCell>
                                    <TableCell sx={{width: 140}}>Coincidencia</TableCell>
                                    <TableCell sx={{width: 100}}/>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {proposals.map((p, i) => (
                                    <TableRow key={i} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>{dateToString(p.movementDate)}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {p.movementDescription}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{fontVariantNumeric: "tabular-nums"}}>{fmtEur(p.movementAmount)}</TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                {typeChip(p)}
                                                {billLink(p)}
                                            </Box>
                                            <Typography variant="caption" color="text.secondary">{dateToString(p.proposedDate)}</Typography>
                                        </TableCell>
                                        <TableCell>{p.proposedCounterparty}</TableCell>
                                        <TableCell align="right" sx={{fontVariantNumeric: "tabular-nums"}}>{fmtEur(p.proposedAmount)}</TableCell>
                                        <TableCell sx={{minWidth: 120}}>
                                            <ScoreBar score={p.matchScore ?? 0}/>
                                        </TableCell>
                                        <TableCell>{action(p)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Panel>
            )}
        </Page>
    );
};
